import { useMemo } from 'react'

import { hasStudyDocEmbeds } from '@/components/study-doc'

import { composeRunStudyDoc } from './composeRunStudyDoc'
import { ThreadStudyDocParts } from './composeThreadStudyDoc'
import { useRunResults } from './useRunResults'
import { useRunTemplate } from './useRunTemplate'

interface StudyDocArtifact {
  runToken?: string | null
  body?: string | null
}

/**
 * The `{{plot:…}}` / `{{quote:…}}` lookups for ONE artifact, resolved from the
 * run that wrote it.
 *
 * An artifact body carries embed tokens; the values behind them live on the
 * run. Every surface that renders a body therefore needs the run, and each one
 * used to answer that differently: the page composed lookups from whatever run
 * it was currently on, and the inline and rail cards had no lookups at all, so
 * a report with plots rendered `Plot: <uuid>` boxes in the reading column no
 * matter which run it came from. This is the one answer: ask the artifact
 * which run it belongs to.
 *
 * Costs nothing for the ordinary case. A body with no embed tokens, or an
 * artifact with no run (a living document the reader wrote), fetches nothing,
 * and the run and template caches are keyed by token, so several cards from
 * one run share a single request with each other and with the page.
 */
export function useArtifactStudyDoc(
  artifact: StudyDocArtifact | null | undefined,
): ThreadStudyDocParts | undefined {
  const body = artifact?.body ?? ''
  const needsLookups = Boolean(artifact?.runToken) && hasStudyDocEmbeds(body)
  const runToken = needsLookups ? (artifact?.runToken ?? null) : null

  const { run } = useRunResults(runToken)
  const { template } = useRunTemplate(run?.templateToken ?? null)

  return useMemo(
    () => (runToken ? composeRunStudyDoc(run, template) : undefined),
    [runToken, run, template],
  )
}
